import { useState, useEffect } from 'react';
import { useAuth } from 'src/components/AuthProvider/AuthProvider';
import { getCreditCards } from 'src/components/common/api/getCreditCardsAPI';

export enum CreditCardEnrollmentValue {
  NOT_ENROLLED = 'not enrolled in credit card usage',
  NO_PERMISSION = 'ignore_admin lacks permissions to access payment actions',
  ENROLLED_WITH_CARD = 'credit card usage enabled_added credit card',
  ENROLLED_WITHOUT_CARD = 'credit card usage enabled_no credit card added',
}

export interface CreditCardEnrollmentData {
  corpadminCreditcardenrollment?: string;
}

export type UseCreditCardEnrollmentReturn = {
  isLoading: boolean;
  hasError: boolean;
  enrollmentData: CreditCardEnrollmentData;
};

export const getCardEnrolmentMessage = (
  hasCard: boolean
): CreditCardEnrollmentValue =>
  hasCard
    ? CreditCardEnrollmentValue.ENROLLED_WITH_CARD
    : CreditCardEnrollmentValue.ENROLLED_WITHOUT_CARD;

export const useCreditCardEnrollmentData =
  (): UseCreditCardEnrollmentReturn => {
    const {
      corpId,
      corpData,
      enterpriseData: { isInternalAdminUser, isFinancialAdminUser },
    } = useAuth();

    const [enrollmentValue, setEnrollmentValue] = useState<string | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    const [hasError, setHasError] = useState(false);

    const invoicePaymentMethod = corpData?.invoicePaymentMethod;
    const isCreditCardPrimary = invoicePaymentMethod === 'CREDIT_CARD_PRIMARY';
    const canAccessCreditCard = isInternalAdminUser || isFinancialAdminUser;

    useEffect(() => {
      if (!invoicePaymentMethod) {
        return;
      }

      if (!canAccessCreditCard) {
        setEnrollmentValue(CreditCardEnrollmentValue.NO_PERMISSION);
        return;
      }

      if (!isCreditCardPrimary) {
        setEnrollmentValue(CreditCardEnrollmentValue.NOT_ENROLLED);
        return;
      }

      if (!corpId) {
        return;
      }

      async function fetchCreditCard(): Promise<void> {
        try {
          setIsLoading(true);
          const cards = await getCreditCards(corpId, isInternalAdminUser);
          setEnrollmentValue(
            getCardEnrolmentMessage(Array.isArray(cards) && cards.length > 0)
          );
        } catch {
          // eslint-disable-next-line no-console
          console.warn(
            'Failed to fetch credit card enrollment status for analytics'
          );
          setHasError(true);
        } finally {
          setIsLoading(false);
        }
      }

      fetchCreditCard();
    }, [
      corpId,
      invoicePaymentMethod,
      isCreditCardPrimary,
      canAccessCreditCard,
      isInternalAdminUser,
    ]);

    const enrollmentData: CreditCardEnrollmentData =
      enrollmentValue !== null && !hasError
        ? { corpadminCreditcardenrollment: enrollmentValue }
        : {};

    return { isLoading, hasError, enrollmentData };
  };
